/**
 * Every Font Awesome icon the app names by class string ("fal fa-chevron-right"), as inline-SVG
 * definitions. This replaces the Font Awesome Kit (kit JS + webfonts) the app used to load from
 * kit.fontawesome.com.
 *
 * One deep import per icon, so a bundle only ever carries these glyphs, never a whole pack.
 * To add an icon: import it from the pack that matches its prefix (fal = light, far = regular,
 * fas = solid, fad = duotone) and add it to ICONS. Aliases ("balance-scale" for "scale-balanced")
 * are indexed automatically from each definition, so data can keep using the older names.
 *
 * Kit custom icons (`fak fa-*`) do not ship in the npm packs; their definitions, extracted from the
 * kit, live in faKitIcons.ts.
 */
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { KIT_ICONS } from "./faKitIcons";

import { faCircle as fadCircle } from "@fortawesome/pro-duotone-svg-icons/faCircle";

import { faArrowsToCircle } from "@fortawesome/pro-light-svg-icons/faArrowsToCircle";
import { faArrowTrendDown } from "@fortawesome/pro-light-svg-icons/faArrowTrendDown";
import { faArrowUpRightFromSquare } from "@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare";
import { faBell } from "@fortawesome/pro-light-svg-icons/faBell";
import { faBrain } from "@fortawesome/pro-light-svg-icons/faBrain";
import { faBuildingColumns } from "@fortawesome/pro-light-svg-icons/faBuildingColumns";
import { faBullhorn } from "@fortawesome/pro-light-svg-icons/faBullhorn";
import { faCalendarCheck } from "@fortawesome/pro-light-svg-icons/faCalendarCheck";
import { faCalendarDays } from "@fortawesome/pro-light-svg-icons/faCalendarDays";
import { faChartLine } from "@fortawesome/pro-light-svg-icons/faChartLine";
import { faChartLineDown } from "@fortawesome/pro-light-svg-icons/faChartLineDown";
import { faChartPieSimple } from "@fortawesome/pro-light-svg-icons/faChartPieSimple";
import { faChevronRight } from "@fortawesome/pro-light-svg-icons/faChevronRight";
import { faCode } from "@fortawesome/pro-light-svg-icons/faCode";
import { faCoins } from "@fortawesome/pro-light-svg-icons/faCoins";
import { faDatabase } from "@fortawesome/pro-light-svg-icons/faDatabase";
import { faDollarSign } from "@fortawesome/pro-light-svg-icons/faDollarSign";
import { faEarthEurope } from "@fortawesome/pro-light-svg-icons/faEarthEurope";
import { faFileCertificate } from "@fortawesome/pro-light-svg-icons/faFileCertificate";
import { faGlobe } from "@fortawesome/pro-light-svg-icons/faGlobe";
import { faLightbulb } from "@fortawesome/pro-light-svg-icons/faLightbulb";
import { faListOl } from "@fortawesome/pro-light-svg-icons/faListOl";
import { faNewspaper } from "@fortawesome/pro-light-svg-icons/faNewspaper";
import { faOilCan } from "@fortawesome/pro-light-svg-icons/faOilCan";
import { faPercent } from "@fortawesome/pro-light-svg-icons/faPercent";
import { faSatelliteDish } from "@fortawesome/pro-light-svg-icons/faSatelliteDish";
import { faScaleBalanced } from "@fortawesome/pro-light-svg-icons/faScaleBalanced";
import { faSplit } from "@fortawesome/pro-light-svg-icons/faSplit";
import { faTableColumns } from "@fortawesome/pro-light-svg-icons/faTableColumns";
import { faUsers } from "@fortawesome/pro-light-svg-icons/faUsers";
import { faUserTie } from "@fortawesome/pro-light-svg-icons/faUserTie";
import { faWandMagicSparkles } from "@fortawesome/pro-light-svg-icons/faWandMagicSparkles";
import { faXmark } from "@fortawesome/pro-light-svg-icons/faXmark";

const ICONS: IconDefinition[] = [
    // duotone (fad)
    fadCircle,
    // light (fal)
    faArrowsToCircle,
    faArrowTrendDown,
    faArrowUpRightFromSquare,
    faBell,
    faBrain,
    faBuildingColumns, // also "university"
    faBullhorn,
    faCalendarCheck,
    faCalendarDays, // also "calendar-alt"
    faChartLine,
    faChartLineDown,
    faChartPieSimple, // also "chart-pie-alt"
    faChevronRight,
    faCode,
    faCoins,
    faDatabase,
    faDollarSign,
    faEarthEurope, // also "globe-europe"
    faFileCertificate,
    faGlobe,
    faLightbulb,
    faListOl,
    faNewspaper,
    faOilCan,
    faPercent,
    faSatelliteDish,
    faScaleBalanced, // also "balance-scale"
    faSplit,
    faTableColumns,
    faUsers,
    faUserTie,
    faWandMagicSparkles,
    faXmark,
    // ORTEX custom (fak)
    ...KIT_ICONS,
];

/** "<prefix>:<name>" (e.g. "fal:balance-scale") to its definition, canonical names and aliases both. */
const REGISTRY = new Map<string, IconDefinition>();
for (const def of ICONS) {
    REGISTRY.set(`${def.prefix}:${def.iconName}`, def);
    for (const alias of def.icon[2]) {
        if (typeof alias === "string") REGISTRY.set(`${def.prefix}:${alias}`, def);
    }
}

export function lookupFaIcon(prefix: string, name: string): IconDefinition | undefined {
    return REGISTRY.get(`${prefix}:${name}`);
}
